/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — multi-step wizard for heli-guide bookings:
 *              personal data, activity/consent declarations, Yousign
 *              e-signature, optional payment
 *   extends:   Controller (@ember/controller)
 *   template:  none
 *   qp:        contact_id, project_id
 *   key:       loadDataTask, saveStepTask, reqAvanzatiTask, createYouSignTask,
 *              createPaymentTask, nextStepTask, checkFields, setPHData,
 *              doConsenso, negoConsenso
 *   uses:      applicationController (@controller('application')); router/
 *              intl/session/store/log/notify services; beebee-base/lib/utility
 *              (beebeeAjaxGet/Post, notifyUserError, bbParam, escapeHtml,
 *              loadScript), beebee-base/lib/get-config; global window.Yousign
 *              (loaded on demand from cdn.yousign.tech)
 *   siblings:  routes/heliguides-sign-request.js (re-exports guide-personal-
 *              data's route), templates/heliguides-sign-request.hbs
 *   tests:     wave5-coverage-test.js, wave9-coverage-test.js,
 *              wave15-coverage-test.js, wave16-coverage-test.js,
 *              wave17-coverage-test.js, guide-heli-controllers-test.js
 *   gotchas:   `altreAttivita` must stay @tracked — it is two-way bound at
 *              heliguides-sign-request.hbs:299 and was previously undeclared
 *              (silent no-op writes). nextStepTask tests saveStepTask's
 *              result `=== false` (success resolves `undefined`) so a
 *              swallowed POST failure blocks the Yousign request instead of
 *              firing it against data that was never stored.
 *
 * Step wizard (intro → data → signature) for heli-guide bookings: reuses the
 * PeakShunter PHCustomData timeline, adds activity/experience/consent
 * declarations, and drives a Yousign e-signature request before an optional
 * payment step.
 */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';

import { beebeeAjaxGet, beebeeAjaxPost, bbParam, escapeHtml } from 'beebee-base/lib/utility';
import { notifyUserError } from 'beebee-base/lib/utility';
import { loadScript } from 'beebee-base/lib/utility/load-script';

import { action, computed, set } from '@ember/object';

import { task, timeout } from 'ember-concurrency';

import { inject as iService } from '@ember/service';

import { inject as iController } from '@ember/controller';

import getConfig from 'beebee-base/lib/get-config';
export default class GuideSignRequestController extends Controller {
  @iService notify;
  queryParams = ['contact_id', 'project_id'];
  @iService router;
  @iService intl;
  @iService session;
  @iService store;
  @iService log;
  @iController('application') applicationController;

  // Two-way bound at heliguides-sign-request.hbs:299; was never declared.
  @tracked altreAttivita = null;

  attivitaPartecipareIT = [
    'Freeride',
    'Sci Alpinismo',
    'Racchette da neve',
    'Arrampicata su Ghiaccio',
    'Alpinismo',
    'Arrampicata',
    'Canyoning',
    'Heliski',
    'Via Ferrata',
    'Trekking',
  ];

  attivitaPartecipareEN = [
    'Freeride',
    'Ski Mountaineering',
    'Snowshoes',
    'Ice climbing',
    'Mountaineering',
    'Climbing',
    'Canyoning',
    'Heliski',
    'Via Ferrata',
    'Trekking',
  ];

  attivitaPartecipareDE = [
    'Freeride',
    'Skibergsteigen',
    'Schneeschuhe',
    'Eisklettern',
    'Bergsteigen',
    'Klettern',
    'Canyoning',
    'Heliski',
    'Klettersteig',
    'Trekking',
  ];

  attivitaExpIT = [
  'Freeride',
  'Sci Alpinismo',
  'Racchette da neve',
  'Arrampicata su Ghiaccio',
  'Alpinismo',
  'Arrampicata',
  'Canyoning',
  'Heliski',
  'Via Ferrata',
  'nessuna esperienza'
  ];

  attivitaExpEN = [
  'Freeride',
  'Ski Touring',
  'Snowshoeing',
  'Ice climbing',
  'Mountaineering',
  'Climbing',
  'Canyoning',
  'Heliski',
  'Via Ferrata',
  'no experience'
  ];

  attivitaExpDE = [
  'Freeride',
  'Klettern',
  'Skitouren',
  'Canyoning',
  'Schneeschuhwandern',
  'Heliski',
  'Eisklettern',
  'Klettersteig',
  'Bergsteigen',
  'keine Erfahrung'
  ];

  livSciAlpIT = ['Scarsa', 'Discreta', 'Buona', 'Ottima'];
  livSciAlpEN = ['Poor', 'Average', 'Good', 'Very good'];
  livSciAlpDE = ['Anfänger', 'Durchschnittliche', 'Gut', 'Sehr gut'];

  steps = [
    {
      name: 'heliguide.intro',
      number: 1,
      status: 'active',
    },
    {
      name: 'heliguide.data',
      number: 2,
      status: '',
    },
    {
      name: 'heliguide.signature',
      number: 3,
      status: '',
    },
  ];

  @computed('applicationController.lang').readOnly()
  get attivitaList() {
    const retArr = [];
    let langArr = this.attivitaExpIT;
    const lang = this.applicationController.lang?.toLowerCase() || 'it-it';

    if (lang === 'de-de') {
      langArr = this.attivitaExpDE;
    } else if (lang === 'en-gb' || lang === 'en-us') {
      langArr = this.attivitaExpEN;
    }

    langArr.forEach((l) => {
      retArr.push({ name: l, checked: false })
    });

    return retArr;
  }

  @computed('applicationController.lang').readOnly()
  get attivitaPartecipareList() {
    const retArr = [];
    let langArr = this.attivitaPartecipareIT;
    const lang = this.applicationController.lang?.toLowerCase() || 'it-it';

    if (lang === 'de-de') {
      langArr = this.attivitaPartecipareDE;
    } else if (lang === 'en-gb' || lang === 'en-us') {
      langArr = this.attivitaPartecipareEN;
    }

    langArr.forEach((l) => {
      retArr.push({ name: l, checked: false })
    });

    return retArr;
  }

  @computed('applicationController.lang').readOnly()
  get livSciAlp() {
    const lang = this.applicationController.lang?.toLowerCase() || 'it-it';

    if (lang === 'de-de') {
      return this.livSciAlpDE;
    } else if (lang === 'en-gb' || lang === 'en-us') {
      return this.livSciAlpEN;
    }

    return this.livSciAlpIT;
  }

  @computed('steps.@each.status')
  get stepSelected() {
    return this.steps.find((i) => i.status === 'active');
  }

  @(task(function* () {
    yield timeout(250);

    if (!this.session.token || !this.project_id) {
      return;
    }

    try {
      const PHCustomData = yield beebeeAjaxGet(
                    `${getConfig(this).APP.urlApi}/peakshunter_timeline/${this.project_id}`,
                    this.session.token
                    );

      const prenoData = yield beebeeAjaxGet(
                    `${getConfig(this).APP.urlApi}/guidemonterosa_prenotazione/${this.project_id}`,
                    this.session.token
                    );

      if (!prenoData?.data) {
        this.log.warn('[heliguides-sign-request] guidemonterosa_prenotazione returned no data:', prenoData);
        return;
      }
      this.store.pushPayload(prenoData);
      let projectData = this.store.peekRecord('project', prenoData.data.id);

      const langMap = {
        francese: 'en-gb',
        inglese: 'en-gb',
        italiano: 'it-it',
        tedesco: 'de-de',
      };

      let lang = PHCustomData?.prenotazione?.lang;

      if (PHCustomData?.loggato && Array.isArray(PHCustomData?.principale)) {
        PHCustomData.principale = PHCustomData.principale.filter(
          (item) => item.id === PHCustomData.loggato,
        );
      }

      if (lang && langMap[lang]) {
        set(this.applicationController, 'lang', langMap[lang]);
        this.applicationController.switchLangTask.perform(langMap[lang]);
      }

      this.setPHData(PHCustomData);
      set(this, 'projectData', projectData);

      this.applicationController.setProperties({
        'content.open_organization': this.store.queryRecord(
          'organization',
          'open',
        ),
      });
    } catch (e) {
      this.log.warn('loadDataTask: load failed', e);
    }
  }).restartable())
  loadDataTask;

  setPHData(PHCustomData) {
    /*if(!PHCustomData.utente_principale) {
        PHCustomData.partecipanti = PHCustomData.partecipanti.filter(item => item.id === PHCustomData.loggato);
        PHCustomData.principale = [];
    }*/

    set(this, 'PHCustomData', PHCustomData);
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  recreateTextAddressTask(p) {
    let {
      location_address_street,
      location_address_house,
      location_address_postal_code,
      location_address_city,
      location_address_country,
    } = p;

    let newAddressText = `${location_address_street ?? ''} ${location_address_house ?? ''}<br/>${location_address_postal_code ?? ''} ${location_address_city ?? ''}<br/>${location_address_country ?? ''}`;
    return newAddressText;
  }

  checkFields() {
    var self = this;

    const requiredFieldsPrincipale = [
      'last_name',
      'first_name',
      'mail',
      'mobile',
      'data_nascita',
      'luogo_nascita',
      'location_address_street',
      'location_address_house',
      'location_address_postal_code',
      'location_address_city',
      'location_address_country',
      'location_address_country_code',
    ];

    let foundEmpty = false;

    (this.PHCustomData.principale || []).forEach((principale) => {
      set(principale, 'requiredEmpty', {});

      requiredFieldsPrincipale.forEach((field) => {
        if (!principale[field] || principale[field].trim() === '') {
          if (field.includes('location_')) {
            set(principale.requiredEmpty, 'address', true);
          } else {
            set(principale.requiredEmpty, field, true);
          }
          foundEmpty = true;
        }

        if (field === 'mail' && !self.isValidEmail(principale[field])) {
          set(principale.requiredEmpty, field, true);
          foundEmpty = true;
        }

        if (field === 'mobile' && !principale['isMobileValid']) {
          set(principale.requiredEmpty, field, true);
          foundEmpty = true;
        }

        set(
          principale,
          'location_address_text',
          self.recreateTextAddressTask(principale),
        );
      });
    });

    const attivitaSelected = this.attivitaList.filter(
      (item) => item.checked === true,
    );
    const attivitaPartSelected = this.attivitaPartecipareList.filter(
      (item) => item.checked === true,
    );

    set(this, 'dichiaraErrors', null);
    const errors = {};
    //['condizioneFisica', 'livelloAlpinistico', 'livelloSci', 'consensoTrattamento', 'consensoComunicazione', 'consensoDiffusione', 'consensoSensibili']
    ['condizioneFisica', 'livelloAlpinistico', 'livelloSci'].forEach(
      (variable) => {
        if (!this[variable]) {
          errors[variable] = true;
        }
      },
    );

    if (attivitaSelected.length === 0) {
      errors['attivitaSelected'] = true;
    }

    if (attivitaPartSelected.length === 0) {
      errors['attivitaPartSelected'] = true;
    }

    const isDatiPersonaliIncompleti = Object.keys(errors).length > 0;
    const isAttivitaNonSelezionate =
      attivitaSelected.length === 0 || attivitaPartSelected.length === 0;

    set(this, 'dichiaraErrors', errors);
    if (isDatiPersonaliIncompleti || isAttivitaNonSelezionate) {
      foundEmpty = true;
    }

    return foundEmpty;
  }

  copiaCampiLocationAddress(principale, partecipante) {
    if (!principale || !partecipante) {
      return;
    }

    const indexPartecipante = partecipante.findIndex(
      (item) => item.address_id === principale.address_id,
    );
    if (indexPartecipante !== -1) {
      // Copia i campi location_address_* dal principale al partecipante
      partecipante[indexPartecipante].location_address_text =
        principale.location_address_text;
      partecipante[indexPartecipante].location_address_street =
        principale.location_address_street;
      partecipante[indexPartecipante].location_address_house =
        principale.location_address_house;
      partecipante[indexPartecipante].location_address_postal_code =
        principale.location_address_postal_code;
      partecipante[indexPartecipante].location_address_city =
        principale.location_address_city;
      partecipante[indexPartecipante].location_address_country =
        principale.location_address_country;
      partecipante[indexPartecipante].location_address_country_code =
        principale.location_address_country_code;
    }

    return partecipante;
  }

  @(task(function* (stepNumber) {
    yield timeout(250);

    const principaleArr = this.PHCustomData?.principale ?? [];
    for (let i = 0; i < principaleArr.length; i++) {
      this.copiaCampiLocationAddress(
        principaleArr[i],
        this.PHCustomData?.partecipanti ?? [],
      );
    }

    try {
      yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/peakshunter_booking_step`,
        this.session.token,
        {
          timeline: this.PHCustomData,
          step: stepNumber,
        });
    } catch (e) {
      this.log.error('saveStepTask failed', e);
      notifyUserError(this.notify, e, this.intl);
      // Report the refusal to the caller: a settled task is not a stored step.
      return false;
    }
  }).restartable())
  saveStepTask;

  @(task(function* (p) {
    yield timeout(250);

    try {
      const PHCustomData = yield beebeeAjaxPost(
      `${getConfig(this).APP.urlApi}/peakshunter_send_request`,
      this.session.token,
      {
          contact_id: p.id,
          project_id: this.project_id,
          mail: p.mail,
          product_name: this.PHCustomData?.prenotazione?.product_name,
          contact_name: `${p.first_name} ${p.last_name}`,
        });

      this.setPHData(PHCustomData);
      this.notify.success('Richiesta inviata');
    } catch (e) {
      this.notify.error(
        'Impossibile inviare la richiesta',
      );
    }
  }).restartable())
  reqAvanzatiTask;

  @(task(function* (onlyUrl = true) {
    var self = this;

    yield timeout(250);

    //const tlFirma = this.PHCustomData.timeline.find(i => i.name === 'Liberatoria Firmata');
    const tlFirma = this.PHCustomData.principale.find(
      (i) => i.id === this.PHCustomData.loggato,
    );

    if (tlFirma?.liberatoria_firmata) {
      set(this, 'LiberatoriaFirmata', true);
      return;
    }

    if (!window.Yousign) {
      try {
        yield loadScript(`https://cdn.yousign.tech/iframe-sdk-1.5.0.min.js`);
      } catch (e) {
        this.log.warn('[heliguides-sign-request] Yousign SDK load failed:', e?.status, e?.statusText);
      }
    }

    set(this, 'contractFileUrl', null);

    const principale = this.PHCustomData?.principale?.[0] || {};
    const { id, first_name, last_name, mobile, mail } = principale;
    const {
      condizioneFisica,
      livelloAlpinistico,
      livelloSci,
      consensoTrattamento,
      consensoComunicazione,
      consensoDiffusione,
      consensoSensibili,
      altreAttivita,
    } = this;
    const idModulo = 1006;
    const lang = this.applicationController.lang?.toLowerCase() || 'it-it';
    const doclang =
      lang === 'de-de'
        ? 'DE'
        : lang === 'it-it'
          ? 'ITA'
          : 'EN';
    const cId = id;
    const prId = this.PHCustomData.prenotazione.id;
    const attivitaSelected = this.attivitaList.filter(
      (item) => item.checked === true,
    );
    const attivitaPartSelected = this.attivitaPartecipareList.filter(
      (item) => item.checked === true,
    );
    const params = {
      full: true,
      token: this.session.token,
      project_id: prId,
      index: idModulo,
      condizioneFisica,
      livelloAlpinistico,
      livelloSci,
      consensoTrattamento,
      consensoComunicazione,
      consensoDiffusione,
      consensoSensibili,
      attivitaSelected,
      attivitaPartSelected,
      altreAttivita,
    };

    const fileUrl = `https://asset.beebeeboard.com/${this.applicationController.get('content.open_organization.organization')}/moduli/${doclang}_liberatoria.php?${bbParam(params)}`;

    if (onlyUrl) {
      set(this, 'contractFileUrl', fileUrl);
      return;
    }

    try {
      const sendData = {
        organization: this.applicationController.get(
          'content.open_organization.organization',
        ),
        organization_name: this.applicationController.get(
          'content.open_organization.name',
        ),
        logo: this.applicationController.get(
          'content.open_organization.thumbnail_url',
        ),
        color: this.applicationController.get(
          'content.open_organization.primary_color',
        ),
        token: this.session.token,
        consenso_id: idModulo,
        contact_id: cId,
        project_id: prId,
        file_url: fileUrl,
        first_name: first_name,
        last_name: last_name,
        mail: mail,
        message: 'Firma Contratto',
        mobile: mobile,
        name: `Liberatoria ${first_name} ${last_name}`,
        language: lang,
        signature_level: 'electronic_signature',
        notify_signatory_by_mail: false,
        signature_authentication_mode: 'otp_sms', //'no_otp', //'otp_sms',
        yousign_sandbox:
          this.session.option_field?.yousign_sandbox || false,
      };

      let sign = yield beebeeAjaxPost(
      'https://asset.beebeeboard.com/utility/yousign/signature_request.php',
      self.session.token,
      sendData);

      if (sign.error) {
        let text = `${escapeHtml(sign.detail?.detail)} <ul>`;
        for (let i = 0; i < (sign.detail?.invalid_params || []).length; i++) {
          text += `<li>${escapeHtml(sign.detail.invalid_params[i].name)}: ${escapeHtml(sign.detail.invalid_params[i].reason)}</li>`;
        }
        text += '</ul>';
        this.notify.warning({
          html: text,
          closeAfter: null,
        });

        this.nextStepTask.perform('back');
      } else if (sign.success) {
        const yousign = new Yousign({
          signatureLink: sign.url,
          iframeContainerId: 'iframe-container',
          isSandbox:
            this.session.option_field?.yousign_sandbox || false,
        });

        yousign.onSuccess((data) => {
          this.notify.success(
            'Signer has successfully signed',
          );
          set(this, 'LiberatoriaFirmata', true);
          //this.createPaymentTask.perform();
        });

        yousign.onError((data) => {
          this.notify.error(
            'Signer encountered an error when signing',
          );
        });

      }
    } catch (e) {
      this.notify.error(
        'Impossibile generare la richiesta',
      );
    }
  }).restartable())
  createYouSignTask;

  @(task(function* () {
    yield timeout(250);

    try {
      const paymentId = yield beebeeAjaxPost(
      `${getConfig(this).APP.urlApi}/guidemonterosa_create_session`,
      this.session.token,
      {
          project_id: this.PHCustomData?.prenotazione?.id,
        });

      if (paymentId?.id) {
        let p = yield this.store.findRecord('payment', paymentId.id, {
          reload: true,
        });

        set(this, 'paymentAPI', p);
      }
    } catch (e) {
      this.notify.error(
        'Impossibile inviare la richiesta',
      );
    }
  }).restartable())
  createPaymentTask;

  @task(function* (direction) {
    const stepActive = this.steps.find((i) => i.status === 'active');

    let foundEmpty = null;
    if (stepActive?.number === 2) {
      foundEmpty = this.checkFields();
    }

    if (foundEmpty) {
      this.notify.warning(
        this.intl.t('empty required field'),
      );
      return;
    }

    if (direction === 'next' && stepActive.number > 1) {
      // Was fire-and-forget: the wizard advanced before the save had even been
      // attempted, so a refusal could never stop it. Wait for the outcome and
      // stop when the server took nothing — otherwise the signature request
      // below goes out against data that was never stored, and the step is
      // marked completed anyway. Test `=== false`: success resolves undefined.
      const saved = yield this.saveStepTask.perform(stepActive.number);
      if (saved === false) {
        return;
      }
    }

    if (direction === 'next') {
      if (stepActive?.number === 2) {
        this.createYouSignTask.perform();
      } /* else if (stepActive?.number === 3) {
        this.createPaymentTask.perform();
      }*/
    }

    const newIndex =
      direction === 'next' ? stepActive.number + 1 : stepActive.number - 1;

    for (let i = 0; i < this.steps.length; i++) {
      let step = this.steps[i];
      if (step.number < newIndex) {
        set(step, 'status', 'completed');
      } else if (step.number === newIndex) {
        set(step, 'status', 'active');
      } else {
        set(step, 'status', '');
      }
    }
  })
  nextStepTask;

  @action
  doConsenso(consenso, value = false) {
    set(this, consenso, value);
    set(this, `nego_${consenso}`, !value);
  }

  @action
  negoConsenso(consenso, value = false) {
    set(this, consenso, !value);
    set(this, `nego_${consenso}`, value);
  }

  @action
  clickLogo() {}
}
